import { Company, IRecruiterProfile } from '@/apis/profileApis'

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'
import { PaginatedData } from '@/apis/api.types'

import {
  RecruitersList,
  RecruitersListDTO,
  getBlockedRecruiterList,
  getRecruiter,
  getRecruiterCompaniesList,
  getRecruiterList,
} from '@/apis/recruitersApis'
import {
  BlockCompanyDTO,
  BlockUserDTO,
  blockCompany,
  blockUser,
} from '@/apis/authApis'
import { invalidateRegisteredUser } from './alumniSlice'

export const recruitersListSlice = createApi({
  reducerPath: 'recruitersListApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Recruiters', 'BlockedRecruiters', 'RecruiterCompanies'],

  endpoints: (builder) => ({
    getRecruitersList: builder.query<
      PaginatedData<IRecruiterProfile>,
      RecruitersListDTO
    >({
      queryFn: async (data) => {
        try {
          return await getRecruiterList(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Recruiters'],
    }),
    getRecruiterCompaniesList: builder.query<
      PaginatedData<Company>,
      RecruitersListDTO
    >({
      queryFn: async (data) => {
        try {
          return await getRecruiterCompaniesList(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['RecruiterCompanies'],
    }),
    getRecruiter: builder.query<IRecruiterProfile, number>({
      queryFn: async (id) => {
        try {
          return await getRecruiter(id)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Recruiters'],
    }),
    blockUser: builder.mutation<{ data: BlockUserDTO }, BlockUserDTO>({
      queryFn: async (data) => {
        try {
          return await blockUser(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(invalidateRegisteredUser()) // Dispatch an action to invalidate another slice
        } catch (error) {
          console.error('Mutation failed', error)
        }
      },
      invalidatesTags: ['Recruiters'],
    }),
    blockCompany: builder.mutation<{ data: BlockCompanyDTO }, BlockCompanyDTO>({
      queryFn: async (data) => {
        try {
          return await blockCompany(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(invalidateRegisteredUser()) // Dispatch an action to invalidate another slice
        } catch (error) {
          console.error('Mutation failed', error)
        }
      },
      invalidatesTags: ['Recruiters', 'RecruiterCompanies'],
    }),
    getBlockedRecruiterList: builder.query<
      PaginatedData<RecruitersList>,
      RecruitersListDTO
    >({
      queryFn: async (data) => {
        try {
          return await getBlockedRecruiterList(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['BlockedRecruiters'],
    }),
  }),
})
export const {
  useGetRecruitersListQuery,
  useGetBlockedRecruiterListQuery,
  useGetRecruiterCompaniesListQuery,
  useGetRecruiterQuery,
  useBlockUserMutation,
  useBlockCompanyMutation,
} = recruitersListSlice

export const resetrecruitersListSlice = () =>
  recruitersListSlice.util.resetApiState()
